import { useState } from 'react';
import { Asset, MetadataFieldType } from '@/api/generatedApi';
import { useUpdateAsset } from '@/hooks/api/assets/useUpdateAsset';
import { useListAllMetadataFields } from '@/hooks/api/metadata/useListAllMetadataFields';
import { useCreateMetadataFields } from '@/hooks/api/metadata/useCreateMetadataFields';
import { useToast } from '@/hooks/useToast';

export const useMetadataSave = (asset?: Asset) => {
  const { showToast } = useToast();
  const { updateAsset, isUpdating } = useUpdateAsset();
  const { createMetadataField, isCreating } = useCreateMetadataFields();
  const { metadataFields } = useListAllMetadataFields();
  const [promotingKey, setPromotingKey] = useState<string | undefined>();

  const saveAsMetadata = async (key: string, value: string) => {
    if (!asset) return;

    setPromotingKey(key);

    try {
      const fieldExists = metadataFields?.some(field => field.name === key);

      if (!fieldExists) {
        await new Promise<void>((resolve, reject) => {
          createMetadataField(
            {
              name: key,
              type: MetadataFieldType.Text,
            },
            {
              onSuccess: () => resolve(),
              onError: (error: Error) => reject(error),
            }
          );
        });
      }

      updateAsset(
        {
          assetId: asset.id,
          data: {
            metadata: {
              [key]: value,
            },
          },
        },
        {
          onSuccess: () => {
            showToast({
              message: `Successfully ${asset.metadata?.[key] ? 'updated' : 'saved'} "${key}" to metadata for asset ${asset.id}`,
              type: 'success',
            });
            setPromotingKey(undefined);
          },
          onError: () => {
            showToast({
              message: `Failed to ${asset.metadata?.[key] ? 'update' : 'save'} "${key}" to metadata for asset ${asset.id}`,
              type: 'error',
            });
            setPromotingKey(undefined);
          },
        }
      );
    } catch {
      showToast({
        message: `Failed to create metadata field "${key}"`,
        type: 'error',
      });
      setPromotingKey(undefined);
    }
  };

  return {
    saveAsMetadata,
    isUpdating,
    isCreating,
    promotingKey,
  };
};
